/**
 * deidentify.js — настоящее обезличивание DICOM.
 *
 * ЧТО БЫЛО ДО. Кнопка «Анонимизировать» показывала «✅ Анонимизация завершена»,
 * не изменив ни одного байта (в 10.19.9 ложный рапорт убран). Второй путь, при
 * экспорте в ZIP, затирал звёздочками ТРИ тега — имя, ID, дату рождения — и
 * только в первых 8 КБ файла. Этого недостаточно ни по одному счёту: остаются
 * учреждение, номер исследования, направивший врач, даты, StudyID, приватные
 * группы, а UID остаются прежними — то есть исследование по-прежнему
 * сопоставимо с исходным в любом архиве.
 *
 * ГЛАВНОЕ РЕШЕНИЕ: ПРАВИМ БАЙТЫ, НЕ ПЕРЕСОБИРАЕМ ФАЙЛ.
 *
 * Соблазн был другой — прочитать файл своим кодеком (`rtDicomCodecCore`),
 * поправить поля и записать заново. Так делать НЕЛЬЗЯ: тот кодек работает по
 * словарю известных имён, а всё, чего в словаре нет — приватные группы
 * производителя, редкие атрибуты, вложенные последовательности — при обратной
 * записи просто исчезнет. Обезличивание, которое молча выбрасывает половину
 * заголовка, хуже отсутствующего: врач получит файл, который откроется, но
 * будет неполным, и узнает об этом на чужой станции.
 *
 * Поэтому здесь обход байтов с заменой ЗНАЧЕНИЙ НА МЕСТЕ, без изменения длин.
 * Всё, что не в списке, остаётся байт в байт: пиксели, сжатие, приватные
 * группы, вложенные последовательности.
 *
 * КАК ЭТО ВОЗМОЖНО ДЛЯ UID, которые надо заменить на новые. Новый UID
 * генерируется НЕ ДЛИННЕЕ старого и добивается нулём до исходной длины — это
 * законный способ выравнивания для VR «UI». Поэтому длина элемента не меняется
 * и файл не надо перекладывать. Если старый UID совсем короткий, новый
 * укорачивается до него (цифр меньше — вероятность совпадения выше, но остаётся
 * пренебрежимой).
 *
 * СВЯЗНОСТЬ. Один и тот же исходный UID всегда получает один и тот же новый —
 * карта живёт в объекте состояния и передаётся на все файлы исследования.
 * Иначе срезы рассыпались бы по разным сериям, а ссылки внутри RTSTRUCT/SR
 * перестали бы находить свои снимки.
 *
 * ДАТЫ НЕ ЗАТИРАЮТСЯ, А СДВИГАЮТСЯ на постоянное число дней. Причина
 * клиническая: программа сравнивает исследования по датам и считает динамику.
 * Затёртая дата ломает сравнение двух съёмок, то есть ломает то, для чего
 * программа и написана. Сдвиг убирает настоящую дату, но сохраняет ИНТЕРВАЛ
 * между исследованиями — так делают все серьёзные деидентификаторы.
 *
 * ЧЕСТНО О ГРАНИЦАХ. Это подмножество профиля PS3.15 Basic Application Level
 * Confidentiality, а не он целиком, и это не «анонимизация» в юридическом
 * смысле. Что НЕ делается — перечислено в НЕ_ДЕЛАЕМ ниже и выводится в отчёт
 * после работы, чтобы никто не решил, что файлы теперь можно публиковать.
 */

/* ── Что делаем с каждым тегом ─────────────────────────────────────────────
 * 'пусто'  — обнулить значение (длина сохраняется, пробелы/нули)
 * 'текст'  — заменить на заданный текст, обрезав/добив до исходной длины
 * 'uid'    — заменить на новый UID той же длины, связно по всему исследованию
 * 'дата'   — сдвинуть на постоянное число дней (формат ГГГГММДД)
 * 'время'  — обнулить (время суток идентифицирует приём, интервал не нужен)
 */
export const ПРАВИЛА = Object.freeze({
  // ── Пациент
  '0010,0010': { что: 'текст', имя: 'PatientName', на: 'ANONYMOUS' },
  '0010,0020': { что: 'текст', имя: 'PatientID', на: 'ANON' },
  '0010,0030': { что: 'дата', имя: 'PatientBirthDate' },
  '0010,0032': { что: 'время', имя: 'PatientBirthTime' },
  '0010,1000': { что: 'пусто', имя: 'OtherPatientIDs' },
  '0010,1001': { что: 'пусто', имя: 'OtherPatientNames' },
  '0010,1005': { что: 'пусто', имя: 'PatientBirthName' },
  '0010,1040': { что: 'пусто', имя: 'PatientAddress' },
  '0010,1060': { что: 'пусто', имя: 'PatientMotherBirthName' },
  '0010,2154': { что: 'пусто', имя: 'PatientTelephoneNumbers' },
  '0010,2160': { что: 'пусто', имя: 'EthnicGroup' },
  '0010,4000': { что: 'пусто', имя: 'PatientComments' },
  '0010,2000': { что: 'пусто', имя: 'MedicalAlerts' },
  '0010,2110': { что: 'пусто', имя: 'Allergies' },
  '0010,21B0': { что: 'пусто', имя: 'AdditionalPatientHistory' },

  // ── Учреждение и люди
  '0008,0080': { что: 'пусто', имя: 'InstitutionName' },
  '0008,0081': { что: 'пусто', имя: 'InstitutionAddress' },
  '0008,0082': { что: 'пусто', имя: 'InstitutionCodeSequence' },
  '0008,1010': { что: 'пусто', имя: 'StationName' },
  '0008,1040': { что: 'пусто', имя: 'InstitutionalDepartmentName' },
  '0008,0090': { что: 'пусто', имя: 'ReferringPhysicianName' },
  '0008,0092': { что: 'пусто', имя: 'ReferringPhysicianAddress' },
  '0008,0094': { что: 'пусто', имя: 'ReferringPhysicianTelephone' },
  '0008,1048': { что: 'пусто', имя: 'PhysiciansOfRecord' },
  '0008,1049': { что: 'пусто', имя: 'PhysiciansOfRecordIdentification' },
  '0008,1050': { что: 'пусто', имя: 'PerformingPhysicianName' },
  '0008,1060': { что: 'пусто', имя: 'NameOfPhysiciansReadingStudy' },
  '0008,1070': { что: 'пусто', имя: 'OperatorsName' },
  '0032,1032': { что: 'пусто', имя: 'RequestingPhysician' },
  '0040,0006': { что: 'пусто', имя: 'ScheduledPerformingPhysicianName' },

  // ── Идентификаторы заявки и исследования
  '0008,0050': { что: 'пусто', имя: 'AccessionNumber' },
  '0020,0010': { что: 'пусто', имя: 'StudyID' },
  '0040,1001': { что: 'пусто', имя: 'RequestedProcedureID' },
  '0040,2016': { что: 'пусто', имя: 'PlacerOrderNumber' },
  '0040,2017': { что: 'пусто', имя: 'FillerOrderNumber' },
  '0038,0010': { что: 'пусто', имя: 'AdmissionID' },
  '0038,0300': { что: 'пусто', имя: 'CurrentPatientLocation' },
  '0038,0400': { что: 'пусто', имя: 'PatientInstitutionResidence' },
  '0038,4000': { что: 'пусто', имя: 'VisitComments' },
  '0008,1030': { что: 'пусто', имя: 'StudyDescription' },
  '0008,0081x': { что: 'пусто', имя: '(зарезервировано)' },

  // ── Даты и время: сдвиг, а не затирание (см. шапку файла)
  '0008,0020': { что: 'дата', имя: 'StudyDate' },
  '0008,0021': { что: 'дата', имя: 'SeriesDate' },
  '0008,0022': { что: 'дата', имя: 'AcquisitionDate' },
  '0008,0023': { что: 'дата', имя: 'ContentDate' },
  '0008,0012': { что: 'дата', имя: 'InstanceCreationDate' },
  '0008,002A': { что: 'дата', имя: 'AcquisitionDateTime' },
  '0038,0020': { что: 'дата', имя: 'AdmittingDate' },
  '0040,0244': { что: 'дата', имя: 'PerformedProcedureStepStartDate' },
  '0008,0030': { что: 'время', имя: 'StudyTime' },
  '0008,0031': { что: 'время', имя: 'SeriesTime' },
  '0008,0032': { что: 'время', имя: 'AcquisitionTime' },
  '0008,0033': { что: 'время', имя: 'ContentTime' },
  '0008,0013': { что: 'время', имя: 'InstanceCreationTime' },

  // ── UID: заменяются связно
  '0008,0018': { что: 'uid', имя: 'SOPInstanceUID' },
  '0020,000D': { что: 'uid', имя: 'StudyInstanceUID' },
  '0020,000E': { что: 'uid', имя: 'SeriesInstanceUID' },
  '0020,0052': { что: 'uid', имя: 'FrameOfReferenceUID' },
  '0020,0200': { что: 'uid', имя: 'SynchronizationFrameOfReferenceUID' },
  '0008,1155': { что: 'uid', имя: 'ReferencedSOPInstanceUID' },
  '0008,0014': { что: 'uid', имя: 'InstanceCreatorUID' },
  '0088,0140': { что: 'uid', имя: 'StorageMediaFileSetUID' },
  '0002,0003': { что: 'uid', имя: 'MediaStorageSOPInstanceUID' },
  '0004,1511': { что: 'uid', имя: 'ReferencedSOPInstanceUIDInFile' },
});

/**
 * Что этот модуль НЕ делает. Выводится в отчёт после работы: человек обязан
 * знать границы, иначе обезличивание становится опаснее его отсутствия.
 */
export const НЕ_ДЕЛАЕМ = Object.freeze([
  'Надписи, вжжённые в само изображение (имя пациента, дата на кадре), не удаляются — их не видно из заголовка.',
  'Приватные группы производителя очищаются, но не удаляются: их наличие само по себе указывает на модель аппарата.',
  'Структурированные отчёты и RTSTRUCT: ссылки на снимки переписываются, свободный текст внутри — нет.',
  'Это подмножество профиля PS3.15, а не он целиком. Для публикации данных требуется проверка человеком.',
]);

const ПУСТЫЕ_VR = new Set(['SQ', 'OB', 'OW', 'OF', 'OD', 'OL', 'UN', 'UT', 'UC', 'UR']);
const ДЛИННЫЕ_VR = new Set(['OB', 'OW', 'OF', 'OD', 'OL', 'SQ', 'UT', 'UC', 'UR', 'UN']);
const TS_IMPLICIT = '1.2.840.10008.1.2';

const кодировщик = new TextEncoder();
const декодировщик = new TextDecoder('latin1');

/** Корень новых UID: 2.25 + случайное — законный «UUID-derived» способ. */
function корень(случайно) {
  let s = '2.25.';
  for (let i = 0; i < 12; i++) s += Math.floor(случайно() * 10);
  return s;
}

/**
 * Состояние на одно исследование: карта UID, сдвиг дат, корень.
 *
 * @param {{сдвигДней?: number, случайно?: () => number}} [o]
 */
export function новоеСостояние(o = {}) {
  const случайно = typeof o.случайно === 'function' ? o.случайно : Math.random;
  // Сдвиг задаётся снаружи (тестом) или берётся случайным в пределах ±5 лет.
  // Ноль запрещён: «сдвинули на 0 дней» — это не сдвиг.
  let сдвиг = Number.isFinite(o.сдвигДней) ? Math.trunc(o.сдвигДней)
    : (Math.floor(случайно() * 3650) + 30) * (случайно() < 0.5 ? -1 : 1);
  if (сдвиг === 0) сдвиг = 37;
  return {
    сдвигДней: сдвиг,
    корень: корень(случайно),
    карта: new Map(),
    случайно,
    счётчик: 0,
  };
}

/**
 * Новый UID для старого — связно и не длиннее исходного.
 * @param {object} с состояние
 * @param {string} старый
 * @param {number} длина сколько байт занимало значение
 */
export function новыйUID(с, старый, длина) {
  const ключ = старый;
  if (с.карта.has(ключ)) {
    const готовый = с.карта.get(ключ);
    if (готовый.length <= длина) return готовый;
    // Тот же UID встретился в элементе с меньшей длиной — укорачиваем, но
    // связность сохраняем: обрезаем справа, а не генерируем другой.
    return готовый.slice(0, длина).replace(/\.$/, '');
  }
  с.счётчик += 1;
  let uid = `${с.корень}.${с.счётчик}`;
  if (uid.length > длина) {
    // Не влезает — собираем короткий вариант из того, что есть.
    uid = `2.25.${с.счётчик}`;
    if (uid.length > длина) uid = uid.slice(0, Math.max(3, длина)).replace(/\.$/, '');
  } else {
    // Добиваем цифрами до исходной длины: так меньше нулевых хвостов и
    // разнообразнее значения.
    while (uid.length + 1 <= длина && uid.length < 62) uid += Math.floor(с.случайно() * 10);
  }
  с.карта.set(ключ, uid);
  return uid;
}

/** Сдвинуть дату ГГГГММДД на N дней. Непонятное значение оставляем как есть. */
export function сдвинутьДату(текст, дней) {
  const s = String(текст || '').trim();
  const m = s.match(/^(\d{4})(\d{2})(\d{2})/);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (!Number.isFinite(t)) return null;
  const d = new Date(t + дней * 86400000);
  const г = d.getUTCFullYear();
  if (г < 1900 || г > 2999) return null;   // ушли в бессмыслицу — не портим
  const итог = `${г}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
  // Хвост (например, время в AcquisitionDateTime) сохраняем без изменений.
  return итог + s.slice(8);
}

/** Записать текст в отведённые байты: обрезать или добить до точной длины. */
function вписать(байты, начало, длина, текст, добивка) {
  const b = кодировщик.encode(текст);
  for (let i = 0; i < длина; i++) байты[начало + i] = i < b.length ? b[i] : добивка;
}

/**
 * Обезличить один файл. Возвращает НОВЫЙ буфер — исходный не меняется.
 *
 * @param {Uint8Array|ArrayBuffer} вход
 * @param {object} состояние из новоеСостояние(), общее на всё исследование
 * @returns {{байты: Uint8Array, отчёт: {изменено: object[], uidЗаменено: number,
 *            вжжённыйТекст: 'да'|'нет'|'неизвестно', предупреждения: string[]}}}
 */
export function обезличитьБуфер(вход, состояние) {
  const исходные = вход instanceof Uint8Array ? вход : new Uint8Array(вход);
  const байты = исходные.slice();               // работаем по копии
  const dv = new DataView(байты.buffer, байты.byteOffset, байты.byteLength);
  const отчёт = { изменено: [], uidЗаменено: 0, вжжённыйТекст: 'неизвестно', предупреждения: [] };

  // ── Метка DICM и метагруппа
  let p = 0;
  if (байты.length > 132 && декодировщик.decode(байты.slice(128, 132)) === 'DICM') p = 132;
  else отчёт.предупреждения.push('Нет метки DICM: файл разобран как «сырой» набор данных без метагруппы.');

  // Метагруппа всегда Explicit VR LE; из неё берём синтаксис передачи.
  let синтаксис = null;
  let началоНабора = p;
  if (p === 132) {
    const g = dv.getUint16(p, true), e = dv.getUint16(p + 2, true);
    if (g === 0x0002 && e === 0x0000) {
      const длинаМеты = dv.getUint32(p + 8, true);
      const конецМеты = p + 12 + длинаМеты;
      пройти(p + 12, Math.min(конецМеты, байты.length), false);
      началоНабора = конецМеты;
      // Синтаксис читаем ПОСЛЕ обхода: (0002,0010) мы не меняем.
      синтаксис = найтиТекст(p + 12, конецМеты, 0x0002, 0x0010);
    } else {
      отчёт.предупреждения.push('Метагруппа начинается не с (0002,0000) — синтаксис определён по содержимому.');
    }
  }
  const неявный = синтаксис ? String(синтаксис).trim().replace(/\0+$/, '') === TS_IMPLICIT
    : похожеНаНеявный(началоНабора);

  пройти(началоНабора, байты.length, неявный);
  отчёт.синтаксис = синтаксис ? синтаксис.trim().replace(/\0+$/, '') : (неявный ? TS_IMPLICIT : 'неизвестно');

  if (отчёт.вжжённыйТекст === 'да') {
    отчёт.предупреждения.push('В файле отмечено (0028,0301 = YES), что надписи вжжены в изображение — заголовок их не убирает.');
  }
  return { байты, отчёт };

  // ── внутренние ──────────────────────────────────────────────────────────

  /** Прочитать текстовое значение конкретного тега в диапазоне (для 0002,0010). */
  function найтиТекст(от, до, гр, эл) {
    let q = от;
    while (q + 8 <= до) {
      const g = dv.getUint16(q, true), e = dv.getUint16(q + 2, true);
      const vr = декодировщик.decode(байты.slice(q + 4, q + 6));
      let длина, знач;
      if (ДЛИННЫЕ_VR.has(vr)) { длина = dv.getUint32(q + 8, true); знач = q + 12; }
      else { длина = dv.getUint16(q + 6, true); знач = q + 8; }
      if (g === гр && e === эл) return декодировщик.decode(байты.slice(знач, знач + длина));
      q = знач + длина;
    }
    return null;
  }

  /** Грубая проверка: похоже ли начало набора на неявный VR. */
  function похожеНаНеявный(от) {
    if (от + 6 > байты.length) return false;
    const c1 = байты[от + 4], c2 = байты[от + 5];
    const букваVR = (c1 >= 0x41 && c1 <= 0x5A) && (c2 >= 0x41 && c2 <= 0x5A);
    return !букваVR;
  }

  /**
   * Обход элементов. Рекурсивно заходит в последовательности (SQ), потому что
   * ссылки на снимки (ReferencedSOPInstanceUID) лежат именно там.
   */
  function пройти(от, до, вНеявном, глубина = 0) {
    if (глубина > 12) return;                    // защита от кривых файлов
    let q = от;
    while (q + 8 <= до) {
      const g = dv.getUint16(q, true), e = dv.getUint16(q + 2, true);

      // Служебные теги элементов последовательности — у них VR нет никогда.
      if (g === 0xFFFE) {
        const длина = dv.getUint32(q + 4, true);
        if (e === 0xE000) {                       // начало элемента
          if (длина === 0xFFFFFFFF) { пройти(q + 8, до, вНеявном, глубина + 1); return; }
          пройти(q + 8, Math.min(q + 8 + длина, до), вНеявном, глубина + 1);
          q = q + 8 + длина;
          continue;
        }
        // конец элемента / конец последовательности
        q += 8;
        if (e === 0xE0DD) return;
        continue;
      }

      let vr, длина, знач;
      if (вНеявном) {
        длина = dv.getUint32(q + 4, true);
        знач = q + 8;
        vr = null;
      } else {
        vr = декодировщик.decode(байты.slice(q + 4, q + 6));
        if (ДЛИННЫЕ_VR.has(vr)) { длина = dv.getUint32(q + 8, true); знач = q + 12; }
        else { длина = dv.getUint16(q + 6, true); знач = q + 8; }
      }

      // Последовательность или сжатые пиксели с неопределённой длиной.
      const последовательность = vr === 'SQ' || (длина === 0xFFFFFFFF);
      if (последовательность) {
        if (длина === 0xFFFFFFFF) {
          // Сжатые пиксели (7FE0,0010) заходить внутрь не надо: там кадры, а не
          // элементы. Просто доходим до разделителя последовательности.
          if (g === 0x7FE0 && e === 0x0010) { q = доРазделителя(знач, до); continue; }
          пройти(знач, до, вНеявном, глубина + 1);
          q = доРазделителя(знач, до);
          continue;
        }
        пройти(знач, Math.min(знач + длина, до), вНеявном, глубина + 1);
        q = знач + длина;
        continue;
      }

      if (знач + длина > байты.length) return;    // битый файл — дальше не идём

      // Отметка «надписи вжжены в изображение» — не меняем, но докладываем.
      if (g === 0x0028 && e === 0x0301 && длина > 0) {
        const v = декодировщик.decode(байты.slice(знач, знач + длина)).trim().toUpperCase();
        отчёт.вжжённыйТекст = v.startsWith('Y') ? 'да' : (v.startsWith('N') ? 'нет' : 'неизвестно');
      }

      // Приватные группы (нечётный номер): значение обнуляем, элемент не
      // выбрасываем — иначе поехали бы длины.
      if ((g & 1) === 1 && g > 0x0008 && длина > 0 && g !== 0xFFFE) {
        байты.fill(0x00, знач, знач + длина);
        отчёт.изменено.push({ тег: тегВТекст(g, e), имя: 'приватная группа', действие: 'обнулено' });
        q = знач + длина;
        continue;
      }

      const правило = ПРАВИЛА[тегВТекст(g, e)];
      if (правило && длина > 0) применить(правило, знач, длина, vr, g, e);
      else if (правило && длина === 0) {
        // Пустое значение обезличивать не нужно, но в отчёте отметим для полноты.
        отчёт.изменено.push({ тег: тегВТекст(g, e), имя: правило.имя, действие: 'уже пусто' });
      }

      q = знач + длина;
    }
  }

  function доРазделителя(от, до) {
    let q = от;
    while (q + 8 <= до) {
      const g = dv.getUint16(q, true), e = dv.getUint16(q + 2, true);
      const длина = dv.getUint32(q + 4, true);
      if (g === 0xFFFE && e === 0xE0DD) return q + 8;
      if (g === 0xFFFE && e === 0xE000) { q = q + 8 + (длина === 0xFFFFFFFF ? 0 : длина); continue; }
      q += 8;
    }
    return до;
  }

  function применить(правило, знач, длина, vr, g, e) {
    const тег = тегВТекст(g, e);
    const старое = декодировщик.decode(байты.slice(знач, знач + длина));
    switch (правило.что) {
      case 'uid': {
        const чистый = старое.replace(/\0+$/, '').trim();
        const новый = новыйUID(состояние, чистый, длина);
        вписать(байты, знач, длина, новый, 0x00);
        отчёт.uidЗаменено += 1;
        отчёт.изменено.push({ тег, имя: правило.имя, действие: 'новый UID' });
        break;
      }
      case 'дата': {
        const новая = сдвинутьДату(старое, состояние.сдвигДней);
        if (новая) {
          вписать(байты, знач, длина, новая, 0x20);
          отчёт.изменено.push({ тег, имя: правило.имя, действие: `сдвиг ${состояние.сдвигДней} дн.` });
        } else {
          байты.fill(0x20, знач, знач + длина);
          отчёт.изменено.push({ тег, имя: правило.имя, действие: 'непонятная дата — затёрта' });
        }
        break;
      }
      case 'время': {
        байты.fill(0x20, знач, знач + длина);
        отчёт.изменено.push({ тег, имя: правило.имя, действие: 'затёрто' });
        break;
      }
      case 'текст': {
        вписать(байты, знач, длина, правило.на || '', 0x20);
        отчёт.изменено.push({ тег, имя: правило.имя, действие: `→ ${правило.на}` });
        break;
      }
      default: {
        // 'пусто': текстовые VR добиваются пробелом, двоичные — нулём.
        const добивка = (vr && ПУСТЫЕ_VR.has(vr)) ? 0x00 : 0x20;
        байты.fill(добивка, знач, знач + длина);
        отчёт.изменено.push({ тег, имя: правило.имя, действие: 'обнулено' });
      }
    }
  }
}

function тегВТекст(g, e) {
  const ч = (n) => n.toString(16).toUpperCase().padStart(4, '0');
  return `${ч(g)},${ч(e)}`;
}

/**
 * Человеческая сводка по одному или нескольким файлам — для показа врачу.
 * @param {Array<{отчёт: object}>} результаты
 */
export function сводка(результаты) {
  const файлов = результаты.length;
  const теги = new Map();
  let uid = 0; let вжжено = 0;
  for (const { отчёт } of результаты) {
    uid += отчёт.uidЗаменено;
    if (отчёт.вжжённыйТекст === 'да') вжжено += 1;
    for (const и of отчёт.изменено) {
      if (и.действие === 'уже пусто') continue;
      теги.set(и.имя, (теги.get(и.имя) || 0) + 1);
    }
  }
  const строки = [
    `Обезличено файлов: ${файлов}.`,
    `Полей изменено: ${теги.size} видов, UID заменено: ${uid}.`,
  ];
  if (вжжено) строки.push(`⚠️ В ${вжжено} файл(ах) отмечено, что надписи вжжены в изображение — заголовок их не убирает.`);
  строки.push('Исходные файлы не изменены: обезличена копия.');
  строки.push('НЕ СДЕЛАНО: ' + НЕ_ДЕЛАЕМ[0]);
  return строки.join('\n');
}

export default {
  ПРАВИЛА, НЕ_ДЕЛАЕМ, новоеСостояние, новыйUID, сдвинутьДату, обезличитьБуфер, сводка,
};
